/** Deterministic owner-scoped settlement applicants.
 *
 * Offers are derived from an authoritative settlement snapshot, current room
 * solution, persistent furniture pids, and a six-hour server-time epoch. They
 * are never a global population: accepting snapshots the chosen candidate into
 * this settlement after the server re-derives and revalidates the same offer.
 */
import { hashSeed } from "../rng.js";
import { homesteadWalkPath, type HomesteadRoomSolution, type RoomFunction } from "../homestead/rooms.js";
import type { PlacedPiece } from "../homestead/types.js";
import { personGender, personName } from "../worldgen/names.js";
import {
  PRODUCTION_RECIPES,
  SETTLEMENT_RESIDENT_TRAITS,
  cloneSettlementState,
  settlementFacilityCapacity,
  settlementFacilityVariant,
  type SettlementProfession,
  type SettlementApplicantPlacementV1,
  type SettlementStateV1,
} from "./production.js";
import { SETTLEMENT_BUILDING_DEFS, type SettlementBuildingVariant } from "./model.js";
import { settlementAppeal, settlementPopulationCap } from "./growth.js";
import { SETTLEMENT_FOUNDER, SETTLEMENT_SECOND_FOUNDER } from "./founding.js";

export const SETTLEMENT_APPLICANT_INTERVAL_MS = 6 * 60 * 60_000;
export const SETTLEMENT_APPLICANT_MAX = 3;
export const SETTLEMENT_APPLICANT_PLACEMENT_MAX = 8;

/** Appeal is never a recruitment gate. It widens the simultaneous identity
 * choice from one to three at explicit, UI-visible thresholds. */
export function settlementApplicantOfferCount(appeal: number): number {
  return Math.min(SETTLEMENT_APPLICANT_MAX, 1 + (appeal >= 8 ? 1 : 0) + (appeal >= 16 ? 1 : 0));
}

interface StarterProfession {
  profession: SettlementProfession;
  variant: SettlementBuildingVariant;
  minStage: SettlementStateV1["stage"];
  /** Productive through a system other than recipes (e.g. the Guild Liaison
   * negotiates orders), so the Rank-1-recipe guard does not apply. */
  nonProduction?: true;
}

const STARTER_PROFESSIONS: Readonly<Partial<Record<RoomFunction, readonly StarterProfession[]>>> = {
  mine: [{ profession: "miner", variant: "mine", minStage: 0 }],
  farm: [{ profession: "farmer", variant: "farm", minStage: 0 }],
  harvester: [{ profession: "synthesist", variant: "harvester", minStage: 0 }],
  salvage: [{ profession: "salvager", variant: "salvage-yard", minStage: 1 }],
  workshop: [
    { profession: "metallurgist", variant: "foundry", minStage: 0 },
    { profession: "fabricator", variant: "workshop", minStage: 1 },
    { profession: "combiner", variant: "combiner", minStage: 1 },
    { profession: "engineer", variant: "garage", minStage: 1 },
    { profession: "outfitter", variant: "outfit-bench", minStage: 2 },
    { profession: "shipwright", variant: "shipyard", minStage: 2 },
  ],
  smithy: [{ profession: "weaponsmith", variant: "smithy", minStage: 1 }],
  kitchen: [
    { profession: "chef", variant: "bakery", minStage: 1 },
    { profession: "chemist", variant: "processor", minStage: 1 },
  ],
  exchange: [
    { profession: "liaison", variant: "guild-hall", minStage: 2, nonProduction: true },
  ],
};

const PROFESSION_MIN_STAGE: Readonly<Partial<Record<SettlementProfession, SettlementStateV1["stage"]>>> = {
  miner: 0, metallurgist: 0, farmer: 0, synthesist: 0, salvager: 1,
  chef: 1, combiner: 1, chemist: 1, fabricator: 1,
  weaponsmith: 1, merchant: 1, engineer: 1, outfitter: 2, shipwright: 2, liaison: 2, steward: 2,
};

interface WorkOpportunity {
  facilityId: string;
  facilityAnchorPid: number;
  workplacePid: number;
  roomFunction: RoomFunction;
  profession: SettlementProfession;
  variant: SettlementBuildingVariant;
}

interface ViablePlacement extends SettlementApplicantPlacementV1 {
  profession: SettlementProfession;
}

function starterAutonomous(entry: StarterProfession, roomFunction: RoomFunction): boolean {
  if (entry.nonProduction) return true;
  return PRODUCTION_RECIPES.some((recipe) => recipe.profession === entry.profession
    && recipe.variants.includes(entry.variant) && recipe.facilityFunctions.includes(roomFunction)
    && recipe.minRank === 1);
}

function opportunities(state: SettlementStateV1, rooms: HomesteadRoomSolution): WorkOpportunity[] {
  const occupied = new Set(state.residents.flatMap((resident) =>
    resident.workplacePid === null ? [] : [resident.workplacePid]));
  const out: WorkOpportunity[] = [];

  for (const facility of state.facilities) {
    const variant = settlementFacilityVariant(facility);
    const professions = SETTLEMENT_BUILDING_DEFS[variant].professions as readonly SettlementProfession[];
    const room = rooms.rooms.find((candidate) =>
      candidate.functionAnchors[facility.roomFunction] === facility.anchorPid);
    if (!room) continue;
    const slots = room.functionSlots[facility.roomFunction].slice(0, settlementFacilityCapacity(facility).workerSlots);
    for (const workplacePid of slots) if (!occupied.has(workplacePid)) {
      for (const profession of professions) {
        if (state.stage < (PROFESSION_MIN_STAGE[profession] ?? 3)) continue;
        out.push({ facilityId: facility.id, facilityAnchorPid: facility.anchorPid, workplacePid,
          roomFunction: facility.roomFunction, profession, variant });
      }
    }
  }

  for (const room of rooms.rooms) {
    for (const roomFunction of room.functions) {
      const facilityAnchorPid = room.functionAnchors[roomFunction];
      const workplacePid = room.functionSlots[roomFunction][0];
      const professions = STARTER_PROFESSIONS[roomFunction] ?? [];
      if (facilityAnchorPid === undefined || workplacePid === undefined || occupied.has(workplacePid)
        || state.facilities.some((facility) => facility.roomFunction === roomFunction
          && facility.anchorPid === facilityAnchorPid)) continue;
      for (const entry of professions) {
        const profession = entry.profession;
        if (state.stage < entry.minStage) continue;
        if (!starterAutonomous(entry, roomFunction)) continue;
        out.push({
          facilityId: `building:${state.beaconId}:${entry.variant}:${facilityAnchorPid}`,
          facilityAnchorPid,
          workplacePid,
          roomFunction,
          profession,
          variant: entry.variant,
        });
      }
    }
  }

  return out.sort((a, b) => {
    const ak = `${a.roomFunction}:${a.facilityAnchorPid}:${a.workplacePid}:${a.variant}:${a.profession}`;
    const bk = `${b.roomFunction}:${b.facilityAnchorPid}:${b.workplacePid}:${b.variant}:${b.profession}`;
    return ak < bk ? -1 : ak > bk ? 1 : 0;
  });
}

/** Refresh the current deterministic offer window. Input is never mutated. */
export function refreshSettlementApplicants(input: SettlementStateV1, rooms: HomesteadRoomSolution,
  pieces: readonly PlacedPiece[], nowMs: number): SettlementStateV1 {
  const state = cloneSettlementState(input);
  const now = Number.isFinite(nowMs) ? Math.max(0, Math.floor(nowMs)) : state.updatedAt;
  const epoch = Math.floor(now / SETTLEMENT_APPLICANT_INTERVAL_MS);
  state.applicantEpoch = epoch;
  state.applicants = [];
  const foundingInProgress = state.residents.some((resident) => resident.id === SETTLEMENT_FOUNDER.id)
    && !state.residents.some((resident) => resident.id === SETTLEMENT_SECOND_FOUNDER.id);
  if (state.residents.length === 0 || state.facilities.length === 0
    || foundingInProgress || state.residents.length >= settlementPopulationCap(state.stage)) return state;

  const occupiedBeds = new Set(state.residents.flatMap((resident) =>
    resident.homePid === null ? [] : [resident.homePid]));
  const beds = rooms.rooms.flatMap((room) => room.functionSlots.dwelling)
    .filter((pid) => !occupiedBeds.has(pid)).sort((a, b) => a - b);
  const work = opportunities(state, rooms);
  if (beds.length === 0 || work.length === 0) return state;
  const byPid = new Map(pieces.map((piece) => [piece.pid, piece]));
  const viable: ViablePlacement[] = [];
  for (const homePid of beds) for (const opportunity of work) {
    const home = byPid.get(homePid);
    const workplace = byPid.get(opportunity.workplacePid);
    if (home && workplace && homesteadWalkPath(rooms, home.p, workplace.p)) {
      viable.push({
        homePid,
        workplacePid: opportunity.workplacePid,
        facilityId: opportunity.facilityId,
        facilityAnchorPid: opportunity.facilityAnchorPid,
        roomFunction: opportunity.roomFunction,
        profession: opportunity.profession,
      });
    }
  }
  if (viable.length === 0) return state;

  const byProfession = new Map<SettlementProfession, ViablePlacement[]>();
  for (const placement of viable) {
    const list = byProfession.get(placement.profession) ?? [];
    list.push(placement);
    byProfession.set(placement.profession, list);
  }
  const groups = [...byProfession].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  const appeal = settlementAppeal(rooms.rooms, state.stage);
  const offerCount = settlementApplicantOfferCount(appeal);
  const accepted = new Set(state.residents.map((resident) => resident.id));
  const start = hashSeed(`${state.beaconId}:applicants:${epoch}:appeal:${appeal}`) % groups.length;
  for (let slot = 0; slot < offerCount; slot++) {
    const id = `applicant:${state.beaconId}:${epoch}:${slot}`;
    if (accepted.has(id)) continue;
    const [profession, group] = groups[(start + slot) % groups.length]!;
    const sorted = group.slice().sort((a, b) => {
      const ak = `${a.homePid}:${a.roomFunction}:${a.facilityAnchorPid}:${a.workplacePid}`;
      const bk = `${b.homePid}:${b.roomFunction}:${b.facilityAnchorPid}:${b.workplacePid}`;
      return ak < bk ? -1 : ak > bk ? 1 : 0;
    });
    const placementStart = hashSeed(`${id}:placement`) % sorted.length;
    const placements = Array.from({ length: Math.min(sorted.length, SETTLEMENT_APPLICANT_PLACEMENT_MAX) },
      (_, index) => sorted[(placementStart + index) % sorted.length]!).map(({ profession: _, ...placement }) => placement);
    const option = placements[0]!;
    const gender = personGender(id);
    const trait = SETTLEMENT_RESIDENT_TRAITS[hashSeed(`${id}:trait`) % SETTLEMENT_RESIDENT_TRAITS.length]!;
    state.applicants.push({
      id,
      name: personName(id, state.beaconId, state.beaconId, gender),
      profession,
      trait,
      ...option,
      placements,
      expiresAt: (epoch + 1) * SETTLEMENT_APPLICANT_INTERVAL_MS,
    });
  }
  return state;
}

/** Catalog guard used by tests: every offered new profession must be productive
 * immediately at the Level-1 building it proposes. */
export function recruitmentCatalogIssues(): string[] {
  const issues: string[] = [];
  for (const [roomFunction, professions] of Object.entries(STARTER_PROFESSIONS)) {
    for (const entry of professions ?? []) {
      if (!starterAutonomous(entry, roomFunction as RoomFunction)) {
        issues.push(`${entry.profession} has no Rank-1/Level-1 ${roomFunction} recipe`);
      }
    }
  }
  return issues;
}
